import config from '../config/config';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import {
    CognitoUser,
    CognitoUserPool,
    CognitoRefreshToken
} from 'amazon-cognito-identity-js';
import { logout } from '../App/Login/loginActions';

let credentialsProvider = null;

function identityCacheKeys() {
    const identityPoolId = config.cognito.IDENTITY_POOL_ID;

    return [
        `aws.cognito.identity-id.${identityPoolId}`,
        `aws.cognito.identity-providers.${identityPoolId}`
    ];
}

export function getCurrentUser() {
    return createUserPool().getCurrentUser();
}

// Method call so tests can stub reload. jsdom's Location.reload is not configurable.
export const windowNavigation = {
    reload() {
        window.location.reload(true);
    }
};

export function cognitoSignOut() {
    const currentUser = getCurrentUser();

    if (!currentUser) {
        return;
    }

    // Local clear only. A callback would call revokeTokens a second time.
    const finish = () => {
        currentUser.signOut();
        windowNavigation.reload();
    };

    currentUser.getSession(function (error) {
        if (error) {
            finish();

            return;
        }

        // Do not call globalSignOut. It revokes every refresh token for this
        // user on the shared emvio pool, including Merchant Portal.
        currentUser.revokeTokens(() => {
            finish();
        });
    });
}

export function getUserTokens(currentUser) {
    if (!currentUser) {
        return Promise.reject('Error: User is not logged in.');
    }
    const username = currentUser.Username || currentUser.username;

    return new Promise((resolve, reject) => {
        const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.APP_CLIENT_ID}.${username}`;
        const tokens = {
            accessToken: window.localStorage.getItem(`${keyPrefix}.accessToken`),
            idToken: window.localStorage.getItem(`${keyPrefix}.idToken`),
            refreshToken: window.localStorage.getItem(`${keyPrefix}.refreshToken`)
        };

        resolve(tokens);
    });
}

export function getCurrentUserTokens(base64Auth) {
    if (base64Auth) {
        return Promise.resolve({ idToken: base64Auth });
    }

    return getUserTokens(getCurrentUser());
}

export function getTokensFromSession(session, currentUser = null) {
    const accessToken = session.getAccessToken().getJwtToken();
    const idToken = session.getIdToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();

    if (currentUser) {
        const username = currentUser.Username || currentUser.username;
        const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.APP_CLIENT_ID}.${username}`;

        window.localStorage.setItem(`${keyPrefix}.accessToken`, accessToken);
        window.localStorage.setItem(`${keyPrefix}.idToken`, idToken);
        window.localStorage.setItem(`${keyPrefix}.refreshToken`, refreshToken);
    }

    return { accessToken, idToken, refreshToken };
}

export function createUser(userName) {
    return new CognitoUser({
        Pool: createUserPool(),
        Username: userName
    });
}

export function createUserPool() {
    return new CognitoUserPool({
        ClientId: config.cognito.APP_CLIENT_ID,
        UserPoolId: config.cognito.USER_POOL_ID
    });
}

export function clearCachedId() {
    identityCacheKeys().forEach((key) => {
        window.localStorage.removeItem(key);
    });
    credentialsProvider = null;
}

export function getAwsCredentials(userToken) {
    const authenticator = `cognito-idp.${config.cognito.REGION}.amazonaws.com/${config.cognito.USER_POOL_ID}`;

    credentialsProvider = fromCognitoIdentityPool({
        clientConfig: { region: config.cognito.REGION },
        identityPoolId: config.cognito.IDENTITY_POOL_ID,
        logins: {
            [authenticator]: userToken
        }
    });

    return credentialsProvider();
}

export function refreshUserTokens(refreshToken, dispatch) {
    const user = getCurrentUser();
    const cognitoRefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken });
    user?.refreshSession(cognitoRefreshToken, (error, session) => {
        if (error) {
            dispatch(logout());
        } else {
            const tokens = getTokensFromSession(session, user);
            getAwsCredentials(tokens.idToken);
        }
    });
}

export function invokeApi({
    body,
    method = 'GET',
    url,
    shouldStringifyBody = true
}, userToken) {

    const headersToSend = {
        Authorization: userToken
    };

    body = (body && shouldStringifyBody) ? JSON.stringify(body) : body;

    return fetch(url, {
        body,
        headers: headersToSend,
        method
    });
}

export default {
    getCurrentUser,
    getUserTokens,
    getCurrentUserTokens,
    getTokensFromSession,
    createUser,
    createUserPool,
    clearCachedId,
    getAwsCredentials,
    refreshUserTokens,
    invokeApi,
    cognitoSignOut
};
